import { useMutation, useQueryClient } from "@tanstack/react-query";

import { traineeActions } from "@fitness-app/app-store";
import { type Trainee, type TraineeProfile } from "@fitness-app/data-models/entities/Trainee";

import { traineeKeys, useTraineeId } from "~/modules/Trainee/TraineeProfile/scope";
import { analyticsService, supabase, trainersApi, useAppDispatch } from "~/store/initializeStore";

// Ten sam zapis co thunk `updateTrainee`, ale bez Reduxa: reducer `updateTrainee.fulfilled` przy zmianie
// imienia nadpisuje `user.firstName/lastName` w profilu Reduxa bez sprawdzenia id, a ten może już należeć
// do klientki innej karty (builder).
export const useUpdateTrainee = () => {
  const traineeId = useTraineeId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (profile: Partial<Trainee>) => {
      const { data, error } = await supabase
        .from("trainee")
        .update({ ...profile, updatedAt: new Date().toISOString() })
        .eq("id", traineeId)
        .select("*")
        .returns<Trainee[]>();

      if (error) {
        throw new Error(error.message);
      }

      // Wiersz odfiltrowany przez RLS wraca jako pusty zbiór, a nie błąd.
      if (!data[0]) {
        throw new Error("Nie udało się zaktualizować klientki - brak uprawnień do edycji tego profilu.");
      }

      analyticsService.track("update_trainee");

      return data[0];
    },
    onSuccess: (row, profile) => {
      queryClient.setQueryData<TraineeProfile>(traineeKeys.profile(traineeId), (old) => {
        if (!old || !row) {
          return old;
        }
        const next = { ...old, ...row };
        if (profile.firstName && old.user) {
          next.user = { ...old.user, firstName: profile.firstName, lastName: profile.lastName || "" };
        }
        return next;
      });
      // Zwrócony wiersz nie ma relacji (np. przypisany trener), więc dociągamy pełny profil.
      void queryClient.invalidateQueries({ queryKey: traineeKeys.profile(traineeId) });
    },
  });
};

// `updateTraineeTags` bierze wszystko z argumentów, a jego reducer sprawdza id profilu, więc zostaje thunk.
export const useUpdateTraineeTags = () => {
  const traineeId = useTraineeId();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tags: string[]) => dispatch(traineeActions.updateTraineeTags({ id: traineeId, tags })).unwrap(),
    onSuccess: (updated) => {
      queryClient.setQueryData<TraineeProfile>(traineeKeys.profile(traineeId), (old) =>
        old ? { ...old, tags: updated.tags, updatedAt: updated.updatedAt } : old,
      );
    },
  });
};

// Thunk `archiveTraineeProfile` dociąga klientkę do Reduxa (fetchTrainee), co nadpisałoby klientkę innej karty.
export const useArchiveTrainee = () => {
  const traineeId = useTraineeId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await trainersApi.post("/trainee/archive-trainee", { id: traineeId });
      analyticsService.track("archive_trainee_profile_manual");
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: traineeKeys.profile(traineeId) }),
  });
};

// Jak `toggleProgramAccess`, ale bez `fetchTrainee` do Reduxa - odświeżamy zapytania karty.
export const useToggleProgramAccess = () => {
  const traineeId = useTraineeId();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ programId, hasAccessToProgram }: { programId: string; hasAccessToProgram: boolean }) => {
      await trainersApi.post("/trainee/toggle-program-access", { traineeId, programId, hasAccessToProgram });
      analyticsService.track("toggle_program_access");
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: traineeKeys.profile(traineeId) });
      void queryClient.invalidateQueries({ queryKey: traineeKeys.program(traineeId) });
    },
  });
};
